import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { Loader } from '@/components/common/Loader'
import { Icon } from '@/components/icons'
import {
  BILL_LOCATION_LABELS,
  BILL_LOCATION_VALUES,
  type BillLocationValue,
} from '@/constants/bill-location'
import dayjs from '@/lib/dayjs'
import { useGetLiffDashboard } from '@/modules/dashboard/liff-dashboard'
import { useGetLiffOutstandingTotal } from '@/modules/bill/services/liff-outstanding'
import { formatNumber, formatPrice } from '@/utils/number'

const BKK = 'Asia/Bangkok'

type PeriodMode = 'month' | 'today'

export function LiffMiniDashboard() {
  const [period, setPeriod] = useState<PeriodMode>('month')
  const [location, setLocation] = useState<'' | BillLocationValue>('')

  const dashboardParams = useMemo(() => {
    const n = dayjs().tz(BKK)
    const loc = location || undefined
    if (period === 'today') {
      const d = n.format('YYYY-MM-DD')
      return { dateFrom: d, dateTo: d, location: loc }
    }
    return {
      dateFrom: n.startOf('month').format('YYYY-MM-DD'),
      dateTo: n.format('YYYY-MM-DD'),
      location: loc,
    }
  }, [period, location])

  const periodLabel =
    period === 'today'
      ? `วันนี้ (${dayjs().tz(BKK).format('D MMM YYYY')})`
      : `เดือนนี้ (${dayjs().tz(BKK).format('MMM YYYY')})`

  const { data, isPending } = useGetLiffDashboard(dashboardParams)
  const { data: outstandingTotal } = useGetLiffOutstandingTotal({
    location: location || undefined,
  })

  const dash = data?.data
  const sell = dash?.sell?.total_price ?? 0
  const buy = dash?.buy?.total_price ?? 0
  const expenses = dash?.all?.expenses_total ?? 0
  const net =
    dash?.all?.net_profit != null ? dash.all.net_profit : sell - buy - expenses
  const isProfit = net >= 0

  const outstandingBills = outstandingTotal?.data?.total_bill ?? 0
  const outstandingAmount =
    outstandingTotal?.data?.total_remaining ?? outstandingTotal?.data?.total_price ?? 0

  return (
    <section className="mx-auto mb-6 max-w-md space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-800">ภาพรวม</h2>
        <span className="text-xs text-slate-500">{periodLabel}</span>
      </div>

      <div className="flex gap-2 rounded-xl bg-white p-1 shadow-sm">
        {(
          [
            { key: 'month' as const, label: 'เดือนนี้' },
            { key: 'today' as const, label: 'วันนี้' },
          ] as const
        ).map((p) => (
          <button
            key={p.key}
            type="button"
            className={`min-h-[40px] flex-1 rounded-lg text-sm font-semibold ${
              period === p.key ? 'bg-slate-800 text-white' : 'text-slate-600'
            }`}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        <LocationChip
          active={!location}
          label="ทุกสาขา"
          onClick={() => setLocation('')}
        />
        {BILL_LOCATION_VALUES.map((loc) => (
          <LocationChip
            key={loc}
            active={location === loc}
            label={BILL_LOCATION_LABELS[loc]}
            onClick={() => setLocation(loc)}
          />
        ))}
      </div>

      {isPending ? (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <StatCard label="ขายออก" value={formatPrice(sell)} unit="บาท" tone="sell" />
          <StatCard label="ซื้อเข้า" value={formatPrice(buy)} unit="บาท" tone="buy" />
          <StatCard label="ค่าใช้จ่าย" value={formatPrice(expenses)} unit="บาท" tone="expense" />
          <StatCard
            label="กำไรสุทธิ"
            value={formatPrice(Math.abs(net))}
            unit="บาท"
            tone={isProfit ? 'profit' : 'loss'}
            prefix={isProfit ? '' : '−'}
          />
        </div>
      )}

      <Link
        to="/liff/outstanding"
        className="flex items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3.5 transition active:scale-[0.99]"
      >
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-xl bg-rose-600 text-white">
            <Icon icon="mdi:file-clock-outline" className="size-6" aria-hidden />
          </span>
          <div>
            <p className="text-sm font-semibold text-rose-900">ใบเสร็จค้างชำระ</p>
            <p className="text-xs text-rose-700">
              {outstandingBills} ใบ · ค้าง {formatNumber(outstandingAmount)} บาท
            </p>
          </div>
        </div>
        <Icon icon="lucide:chevron-right" className="size-6 text-rose-400" aria-hidden />
      </Link>
    </section>
  )
}

function LocationChip({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
        active ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 shadow-sm'
      }`}
    >
      {label}
    </button>
  )
}

function StatCard({
  label,
  value,
  unit,
  tone,
  prefix = '',
}: {
  label: string
  value: string
  unit: string
  tone: 'sell' | 'buy' | 'expense' | 'profit' | 'loss'
  prefix?: string
}) {
  const toneClass = {
    sell: 'border-red-100 bg-white',
    buy: 'border-emerald-100 bg-white',
    expense: 'border-amber-100 bg-white',
    profit: 'border-emerald-200 bg-emerald-50',
    loss: 'border-rose-200 bg-rose-50',
  }[tone]

  const valueClass = {
    sell: 'text-red-700',
    buy: 'text-emerald-700',
    expense: 'text-amber-800',
    profit: 'text-emerald-800',
    loss: 'text-rose-700',
  }[tone]

  return (
    <div className={`rounded-2xl border p-3 shadow-sm ${toneClass}`}>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className={`mt-1 text-lg font-bold leading-tight ${valueClass}`}>
        {prefix}
        {value}
      </p>
      <p className="text-xs text-slate-400">{unit}</p>
    </div>
  )
}
